import { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/lib/api";
import { useCatalog } from "@/context/CatalogContext";

const inputCls = "w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm";
const labelCls = "font-badge text-[10px] tracking-[0.22em] text-[#8A7568]";
const cardCls = "bg-white border border-[#F1DCC5] rounded-2xl p-6";

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocal = (v) => (v ? new Date(v).toISOString() : null);

const effectivePercent = (p, c) => {
  if (c.product_percents?.[p.id] !== undefined) return Number(c.product_percents[p.id]);
  if (c.category_percents?.[p.category] !== undefined) return Number(c.category_percents[p.category]);
  return Number(c.default_percent || 0);
};

// Empty input = inherit (key removed); a number (incl. 0 = excluded) = override.
const PercentMap = ({ value, onChange, id, placeholder, testid }) => (
  <input
    data-testid={testid}
    type="number"
    min="0"
    max="90"
    value={value?.[id] ?? ""}
    placeholder={placeholder}
    onChange={(e) => {
      const next = { ...(value || {}) };
      if (e.target.value === "") delete next[id];
      else next[id] = Number(e.target.value);
      onChange(next);
    }}
    className="w-20 px-2 py-1.5 border border-[#F1DCC5] rounded text-sm text-right"
  />
);

const StatusPill = ({ c, live }) => {
  const label = !c.enabled ? "OFF" : live ? "LIVE NOW" : "SCHEDULED";
  const cls = !c.enabled ? "bg-[#EDE6DC] text-[#8A7568]" : live ? "bg-[#1F7A3A] text-white" : "bg-[#F4CB47] text-[#241C18]";
  return <span data-testid="admin-sale-status" className={`font-badge text-[10px] tracking-[0.2em] px-3 py-1 rounded-full ${cls}`}>{label}</span>;
};

const GeneralCard = ({ c, set, live }) => (
  <section className={cardCls}>
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">SALE CAMPAIGN</div>
        <h2 className="font-serif-display text-2xl mt-1">Discounts & Black Friday</h2>
      </div>
      <StatusPill c={c} live={live} />
    </div>
    <p className="text-xs text-[#8A7568] mt-2 mb-4">
      While the campaign is live, the shop shows the old price crossed out next to the new one, and checkout charges the discounted price. Gift vouchers still work on top.
    </p>
    <label className="flex items-center gap-2 text-sm mb-4">
      <input data-testid="admin-sale-enabled" type="checkbox" checked={!!c.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
      Campaign switched on (runs only between the dates below)
    </label>
    <div className="grid md:grid-cols-4 gap-3">
      <label className="block md:col-span-2">
        <span className={labelCls}>NAME (internal)</span>
        <input data-testid="admin-sale-name" value={c.name || ""} onChange={(e) => set({ name: e.target.value })} className={inputCls} placeholder="Black Friday 2026" />
      </label>
      <label className="block">
        <span className={labelCls}>STARTS</span>
        <input data-testid="admin-sale-start" type="datetime-local" value={toLocal(c.start_at)} onChange={(e) => set({ start_at: fromLocal(e.target.value) })} className={inputCls} />
      </label>
      <label className="block">
        <span className={labelCls}>ENDS</span>
        <input data-testid="admin-sale-end" type="datetime-local" value={toLocal(c.end_at)} onChange={(e) => set({ end_at: fromLocal(e.target.value) })} className={inputCls} />
      </label>
      <label className="block">
        <span className={labelCls}>DISCOUNT FOR EVERYTHING (%)</span>
        <input data-testid="admin-sale-default-percent" type="number" min="0" max="90" value={c.default_percent ?? 0} onChange={(e) => set({ default_percent: Number(e.target.value || 0) })} className={inputCls} />
      </label>
    </div>
    <p className="text-[11px] text-[#8A7568] mt-2">Use 0 here if only some categories or pieces should be discounted.</p>
  </section>
);

const BannerCard = ({ c, set }) => {
  const text = c.banner_text || {};
  return (
    <section className={cardCls}>
      <h3 className="font-serif-display text-xl">Top banner</h3>
      <p className="text-xs text-[#8A7568] mt-1 mb-4">Replaces the announcement bar while the campaign is live.</p>
      <label className="flex items-center gap-2 text-sm mb-4">
        <input data-testid="admin-sale-banner-enabled" type="checkbox" checked={c.banner_enabled !== false} onChange={(e) => set({ banner_enabled: e.target.checked })} />
        Show the banner
      </label>
      <div className="grid md:grid-cols-3 gap-3">
        {[["pt", "PORTUGUESE"], ["en", "ENGLISH"], ["es", "SPANISH"]].map(([k, l]) => (
          <label key={k} className="block">
            <span className={labelCls}>{l}</span>
            <input data-testid={`admin-sale-banner-text-${k}`} value={text[k] || ""} onChange={(e) => set({ banner_text: { ...text, [k]: e.target.value } })} className={inputCls} placeholder="Black Friday · até 30% de desconto" />
          </label>
        ))}
        <label className="block">
          <span className={labelCls}>BACKGROUND</span>
          <input data-testid="admin-sale-banner-bg" type="color" value={c.banner_bg || "#241C18"} onChange={(e) => set({ banner_bg: e.target.value })} className="w-full h-10 mt-1 rounded border border-[#F1DCC5]" />
        </label>
        <label className="block">
          <span className={labelCls}>TEXT COLOUR</span>
          <input data-testid="admin-sale-banner-color" type="color" value={c.banner_color || "#F4CB47"} onChange={(e) => set({ banner_color: e.target.value })} className="w-full h-10 mt-1 rounded border border-[#F1DCC5]" />
        </label>
        <label className="block">
          <span className={labelCls}>LINK WHEN TAPPED</span>
          <input data-testid="admin-sale-banner-link" value={c.banner_link || ""} onChange={(e) => set({ banner_link: e.target.value })} className={inputCls} placeholder="/collection/all" />
        </label>
      </div>
      <div data-testid="admin-sale-banner-preview" className="mt-4 h-7 flex items-center justify-center font-badge text-[12px] tracking-[0.22em] uppercase rounded" style={{ background: c.banner_bg || "#241C18", color: c.banner_color || "#F4CB47" }}>
        {text.pt || "Pré-visualização do banner"}
      </div>
    </section>
  );
};

const CategoriesCard = ({ c, set, categories }) => (
  <section className={cardCls}>
    <h3 className="font-serif-display text-xl">Discount per category</h3>
    <p className="text-xs text-[#8A7568] mt-1 mb-4">Leave empty to use the general discount. 0 = no discount in that category.</p>
    <div className="grid md:grid-cols-2 gap-2">
      {categories.map((cat) => (
        <div key={cat.slug} className="flex items-center justify-between gap-3 border border-[#F1DCC5] rounded px-3 py-2">
          <span className="text-sm">{cat.name_pt}</span>
          <PercentMap testid={`admin-sale-cat-${cat.slug}`} value={c.category_percents} id={cat.slug} placeholder={`${c.default_percent || 0}`} onChange={(v) => set({ category_percents: v })} />
        </div>
      ))}
    </div>
  </section>
);

const ProductsCard = ({ c, set, products }) => {
  const [q, setQ] = useState("");
  const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const list = products.filter((p) => !q || norm(`${p.name} ${p.sku || ""}`).includes(norm(q)));
  return (
    <section className={cardCls}>
      <h3 className="font-serif-display text-xl">Discount per piece</h3>
      <p className="text-xs text-[#8A7568] mt-1 mb-3">Overrides the category and general discount. Leave empty to inherit, 0 to exclude the piece.</p>
      <input data-testid="admin-sale-product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pieces…" className={`${inputCls} mb-3`} />
      <ul className="divide-y divide-[#F1DCC5] max-h-[480px] overflow-y-auto">
        {list.map((p) => {
          const pct = effectivePercent(p, c);
          const newPrice = (Number(p.price) * (100 - pct)) / 100;
          return (
            <li key={p.id} data-testid={`admin-sale-product-${p.slug}`} className="flex items-center gap-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{p.name}</div>
                <div className="text-[11px] text-[#8A7568]">
                  €{Number(p.price).toFixed(2)}
                  {pct > 0 && <span className="text-[#C8361B]"> → €{newPrice.toFixed(2)} (-{pct}%)</span>}
                </div>
              </div>
              <PercentMap testid={`admin-sale-product-input-${p.slug}`} value={c.product_percents} id={p.id} placeholder={`${pct}`} onChange={(v) => set({ product_percents: v })} />
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const AdminSale = () => {
  const [c, setC] = useState(null);
  const [live, setLive] = useState(false);
  const [products, setProducts] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const { categories } = useCatalog();

  useEffect(() => {
    adminApi.get("/admin/sale").then((r) => { setC(r.data); setLive(r.data.live_now); });
    adminApi.get("/admin/products").then((r) => setProducts(r.data || []));
  }, []);

  const set = (patch) => setC((prev) => ({ ...prev, ...patch }));
  const discounted = useMemo(() => (c ? products.filter((p) => effectivePercent(p, c) > 0).length : 0), [c, products]);

  const save = async () => {
    if (c.enabled && c.start_at && c.end_at && new Date(c.end_at) <= new Date(c.start_at)) {
      setMsg({ err: true, text: "The end date must be after the start date." });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      const r = await adminApi.put("/admin/sale", c);
      setC(r.data); setLive(r.data.live_now);
      setMsg({ text: "Saved. The shop updates within a few seconds." });
    } catch (e) {
      setMsg({ err: true, text: e?.response?.data?.detail?.toString?.() || "Save failed" });
    } finally {
      setBusy(false);
    }
  };

  if (!c) return <div className="p-10 text-sm text-[#8A7568]">Loading…</div>;

  return (
    <div className="p-6 md:p-10 space-y-6 pb-28" data-testid="admin-sale">
      <GeneralCard c={c} set={set} live={live} />
      <BannerCard c={c} set={set} />
      <CategoriesCard c={c} set={set} categories={categories} />
      <ProductsCard c={c} set={set} products={products} />
      <div className="sticky bottom-0 bg-[#FFF7E8]/95 backdrop-blur border-t border-[#F1DCC5] -mx-6 md:-mx-10 px-6 md:px-10 py-3 flex items-center gap-4 flex-wrap">
        <button data-testid="admin-sale-save" onClick={save} disabled={busy} className="btn-orange text-xs disabled:opacity-40">
          {busy ? "Saving…" : "Save campaign"}
        </button>
        <span data-testid="admin-sale-summary" className="text-xs text-[#8A7568]">{discounted} of {products.length} pieces discounted</span>
        {msg && <span data-testid="admin-sale-message" className={`text-sm ${msg.err ? "text-[#8B2E14]" : "text-[#1F7A3A]"}`}>{msg.text}</span>}
      </div>
    </div>
  );
};

export default AdminSale;
